import type { ErrorMap } from '@/app/error-registry';

export const TIME_OFF_POLICY_ERROR_MAP: ErrorMap = {
  PUT: {
    'TimeOff.InvalidPolicyEnum': 'timeOff:apiErrors.policy.invalidEnum',
    'TimeOff.InvalidPolicyRange': 'timeOff:apiErrors.policy.invalidRange',
    'TimeOff.InvalidResponseLimit': 'timeOff:apiErrors.policy.invalidResponseLimit',
    'TimeOff.InvalidAdvanceLimit': 'timeOff:apiErrors.policy.invalidAdvanceLimit',
    'TimeOff.AccessDenied': 'timeOff:apiErrors.accessDenied',
    default: 'timeOff:apiErrors.policy.default',
  },
};

export const TIME_OFF_TYPES_ERROR_MAP: ErrorMap = {
  POST: {
    'TimeOff.InvalidTypeName': 'timeOff:apiErrors.types.invalidName',
    'TimeOff.TypeCodeAlreadyExists': 'timeOff:apiErrors.types.codeAlreadyExists',
    'TimeOff.TypeCannotBeHourlyAndConsumeBalance': 'timeOff:apiErrors.types.hourlyAndBalance',
    'TimeOff.AccessDenied': 'timeOff:apiErrors.accessDenied',
    default: 'timeOff:apiErrors.types.default',
  },
  PUT: {
    'TimeOff.InvalidTypeName': 'timeOff:apiErrors.types.invalidName',
    'TimeOff.TypeCannotBeHourlyAndConsumeBalance': 'timeOff:apiErrors.types.hourlyAndBalance',
    'TimeOff.SystemTypeBehaviorLocked': 'timeOff:apiErrors.types.systemBehaviorLocked',
    'TimeOff.AccessDenied': 'timeOff:apiErrors.accessDenied',
    default: 'timeOff:apiErrors.types.default',
  },
  DELETE: {
    'TimeOff.SystemTypeNotDeletable': 'timeOff:apiErrors.types.systemNotDeletable',
    'TimeOff.TypeNotFound': 'timeOff:apiErrors.types.notFound',
    default: 'timeOff:apiErrors.types.default',
  },
};

export const TIME_OFF_MATRIX_ERROR_MAP: ErrorMap = {
  PUT: {
    'TimeOff.InvalidMatrixEntry': 'timeOff:apiErrors.matrix.invalidEntry',
    'TimeOff.MatrixHasGapOrOverlap': 'timeOff:apiErrors.matrix.gapOrOverlap',
    'TimeOff.MatrixMissingOpenEndedEntry': 'timeOff:apiErrors.matrix.missingOpenEnded',
    'TimeOff.AccessDenied': 'timeOff:apiErrors.accessDenied',
    default: 'timeOff:apiErrors.matrix.default',
  },
};

export const TIME_OFF_HOLIDAYS_ERROR_MAP: ErrorMap = {
  POST: {
    'TimeOff.InvalidHolidayName': 'timeOff:apiErrors.holidays.invalidName',
    'TimeOff.HolidayAlreadyExists': 'timeOff:apiErrors.holidays.alreadyExists',
    'TimeOff.AccessDenied': 'timeOff:apiErrors.accessDenied',
    default: 'timeOff:apiErrors.holidays.default',
  },
  PUT: {
    'TimeOff.InvalidHolidayName': 'timeOff:apiErrors.holidays.invalidName',
    'TimeOff.HolidayNotFound': 'timeOff:apiErrors.holidays.notFound',
    'TimeOff.AccessDenied': 'timeOff:apiErrors.accessDenied',
    default: 'timeOff:apiErrors.holidays.default',
  },
  DELETE: {
    'TimeOff.HolidayNotFound': 'timeOff:apiErrors.holidays.notFound',
    'TimeOff.AccessDenied': 'timeOff:apiErrors.accessDenied',
    default: 'timeOff:apiErrors.holidays.default',
  },
};

export const TIME_OFF_CYCLES_ERROR_MAP: ErrorMap = {
  POST: {
    'TimeOff.CycleAlreadyOpen': 'timeOff:apiErrors.cycles.alreadyOpen',
    'TimeOff.MemberHasNoHireDate': 'timeOff:apiErrors.cycles.noHireDate',
    'TimeOff.InvalidHistoricalCycleNumber': 'timeOff:apiErrors.cycles.invalidHistoricalCycleNumber',
    'TimeOff.AccessDenied': 'timeOff:apiErrors.accessDenied',
    default: 'timeOff:apiErrors.cycles.default',
  },
};

export const TIME_OFF_ADJUSTMENTS_ERROR_MAP: ErrorMap = {
  POST: {
    'TimeOff.InvalidAdjustment': 'timeOff:apiErrors.adjustments.invalid',
    'TimeOff.NoOpenCycle': 'timeOff:apiErrors.adjustments.noOpenCycle',
    'TimeOff.AccessDenied': 'timeOff:apiErrors.accessDenied',
    default: 'timeOff:apiErrors.adjustments.default',
  },
};

export const TIME_OFF_REQUESTS_CREATE_ERROR_MAP: ErrorMap = {
  POST: {
    'TimeOff.TypeNotFound': 'timeOff:apiErrors.requests.typeNotFound',
    'TimeOff.TypeInactive': 'timeOff:apiErrors.requests.typeInactive',
    'TimeOff.AdvanceNotAllowed': 'timeOff:apiErrors.requests.advanceNotAllowed',
    'TimeOff.AdvanceLimitExceeded': 'timeOff:apiErrors.requests.advanceLimitExceeded',
    'TimeOff.RequestCrossesCycleBoundary': 'timeOff:apiErrors.requests.crossesCycleBoundary',
    'TimeOff.InsufficientAdvanceNotice': 'timeOff:apiErrors.requests.insufficientAdvanceNotice',
    'TimeOff.OverlappingRequest': 'timeOff:apiErrors.requests.overlapping',
    'TimeOff.NoWorkingDaysInSelection': 'timeOff:apiErrors.requests.noWorkingDays',
    'TimeOff.HoursOutsideWorkSchedule': 'timeOff:apiErrors.requests.hoursOutsideSchedule',
    'TimeOff.AttachmentRequired': 'timeOff:apiErrors.requests.attachmentRequired',
    'TimeOff.DuplicateRequestDay': 'timeOff:apiErrors.requests.duplicateDay',
    'TimeOff.InvalidDayTimeRange': 'timeOff:apiErrors.requests.invalidDayTimeRange',
    default: 'timeOff:apiErrors.requests.default',
  },
};

/**
 * Covers `POST /time-off/admin/historical-requests` (single item, via the
 * global interceptor). The bulk variant never surfaces these as an HTTP
 * error — it always returns `200` with a per-item `errorCode` — so this map
 * is also reused manually (not via `httpClient`) to render bulk-item errors.
 */
export const TIME_OFF_HISTORICAL_REQUESTS_ERROR_MAP: ErrorMap = {
  POST: {
    'TimeOff.MemberNotFound': 'timeOff:apiErrors.historical.memberNotFound',
    'TimeOff.TypeNotFound': 'timeOff:apiErrors.historical.typeNotFound',
    'TimeOff.TypeInactive': 'timeOff:apiErrors.historical.typeInactive',
    'TimeOff.NoWorkingDaysInSelection': 'timeOff:apiErrors.historical.noWorkingDays',
    'TimeOff.OverlappingRequest': 'timeOff:apiErrors.historical.overlapping',
    'TimeOff.CycleNotFound': 'timeOff:apiErrors.historical.cycleNotFound',
    'TimeOff.RequestOutsideCycleRange': 'timeOff:apiErrors.historical.outsideCycleRange',
    'TimeOff.HistoricalBalanceExceeded': 'timeOff:apiErrors.historical.balanceExceeded',
    'TimeOff.FutureDateNotAllowedForHistoricalRequest':
      'timeOff:apiErrors.historical.futureDateNotAllowed',
    'TimeOff.TooManyDaysInHistoricalRequest': 'timeOff:apiErrors.historical.tooManyDays',
    'TimeOff.HistoricalDayNotAWorkingDay': 'timeOff:apiErrors.historical.dayNotWorking',
    'TimeOff.HistoricalCycleAlreadyClosed': 'timeOff:apiErrors.historical.cycleAlreadyClosed',
    'TimeOff.HolidaysNotSeededForPeriod': 'timeOff:apiErrors.historical.holidaysNotSeeded',
    /** Bulk-import-only codes — surfaced per-row by HistoricalImportSection via this same map. */
    'TimeOff.ImportFileTooLarge': 'timeOff:apiErrors.historical.importFileTooLarge',
    'TimeOff.ImportTooManyRows': 'timeOff:apiErrors.historical.importTooManyRows',
    'TimeOff.ImportUnsupportedFormat': 'timeOff:apiErrors.historical.importUnsupportedFormat',
    'TimeOff.ImportEmptyFile': 'timeOff:apiErrors.historical.importEmptyFile',
    'TimeOff.ImportMissingRequiredField': 'timeOff:apiErrors.historical.importMissingField',
    'TimeOff.ImportInvalidDate': 'timeOff:apiErrors.historical.importInvalidDate',
    'TimeOff.ImportInvalidTime': 'timeOff:apiErrors.historical.importInvalidTime',
    'TimeOff.AccessDenied': 'timeOff:apiErrors.accessDenied',
    /** Bulk-only: a genuinely unexpected server-side failure on one row — safe to retry just that row. */
    'TimeOff.UnexpectedError': 'timeOff:apiErrors.historical.unexpectedError',
    default: 'timeOff:apiErrors.historical.default',
  },
};

export const TIME_OFF_APPROVALS_ERROR_MAP: ErrorMap = {
  POST: {
    'TimeOff.NotCurrentApprover': 'timeOff:apiErrors.approvals.notCurrentApprover',
    'TimeOff.RequestAlreadyResolved': 'timeOff:apiErrors.approvals.alreadyResolved',
    default: 'timeOff:apiErrors.approvals.default',
  },
};

export const TIME_OFF_OVERRIDE_ERROR_MAP: ErrorMap = {
  POST: {
    'TimeOff.OverrideReasonRequired': 'timeOff:apiErrors.override.reasonRequired',
    'TimeOff.RequestAlreadyResolved': 'timeOff:apiErrors.override.alreadyResolved',
    default: 'timeOff:apiErrors.override.default',
  },
};

export const TIME_OFF_CANCEL_ERROR_MAP: ErrorMap = {
  DELETE: {
    'TimeOff.RequestNotFound': 'timeOff:apiErrors.cancel.requestNotFound',
    'TimeOff.NotTheRequester': 'timeOff:apiErrors.cancel.notTheRequester',
    'TimeOff.DayAlreadyStarted': 'timeOff:apiErrors.cancel.dayAlreadyStarted',
    'TimeOff.DayAlreadyCancelled': 'timeOff:apiErrors.cancel.dayAlreadyCancelled',
    'TimeOff.RequestDayNotFound': 'timeOff:apiErrors.cancel.dayNotFound',
    default: 'timeOff:apiErrors.cancel.default',
  },
};

export const TIME_OFF_ATTACHMENTS_ERROR_MAP: ErrorMap = {
  POST: {
    'TimeOff.InvalidAttachment': 'timeOff:apiErrors.attachments.invalid',
    default: 'timeOff:apiErrors.attachments.default',
  },
};

export const TIME_OFF_TEAM_ERROR_MAP: ErrorMap = {
  GET: {
    'TimeOff.DateRangeTooWide': 'timeOff:apiErrors.team.dateRangeTooWide',
    default: 'timeOff:apiErrors.team.default',
  },
};

export const TIME_OFF_ADMIN_SUMMARY_ERROR_MAP: ErrorMap = {
  GET: {
    'TimeOff.DateRangeTooWide': 'timeOff:apiErrors.admin.dateRangeTooWide',
    default: 'timeOff:apiErrors.admin.default',
  },
};

export const TIME_OFF_ADMIN_UPCOMING_ERROR_MAP: ErrorMap = {
  GET: {
    'TimeOff.InvalidUpcomingDaysWindow': 'timeOff:apiErrors.admin.invalidUpcomingDaysWindow',
    default: 'timeOff:apiErrors.admin.default',
  },
};
